import { Link } from "react-router-dom";
import { useStoreCount } from "@/hooks/useStoreCount";
import { BookCallButton } from "@/components/shared/BookCallButton";
import {
  Map, Store, RefreshCw, KeyRound, Layers, Timer, Rocket, Users, Code2, Headset, Building2, LayoutDashboard,
  BarChart3, BookOpen, GraduationCap, UserSearch, ShoppingCart, Search, PartyPopper, Megaphone, TrendingUp, Sofa, Smile, Handshake,
  Plug, PackageCheck, Coins, LifeBuoy, Clock,
} from "lucide-react";

const OPTIONS = [
  [Store, "Open a new store", "Start from scratch in any city, with help choosing the location."],
  [RefreshCw, "Convert your store", "Already run a phone or retail store? Convert it to Total Wireless."],
  [KeyRound, "Ready-to-go stores", "Step into a pre-built store we already have available.", "/ready-to-go-stores"],
  [Layers, "Grow to multi-store", "Add locations as you grow — we scale with you."],
];
const SUPPORT = [
  [Users, "Field reps", "On-the-ground reps who visit and coach your stores."],
  [Building2, "Office staff", "A team ready to talk with new dealers every day."],
  [Headset, "Real-time helpdesk", "Escalate or resolve store issues in real time."],
  [Code2, "In-house development team", "We build and improve the tools our dealers use."],
];
const PORTAL = [
  [LayoutDashboard, "Dealer dashboard"], [BarChart3, "Sales reporting & store rankings"], [BookOpen, "Knowledge Base"],
  [GraduationCap, "Training"], [UserSearch, "Hiring help through our Careers page"], [ShoppingCart, "Marketplace"],
];
const MARKETING = [
  [Search, "Local SEO support", "A Google-ready store page with photos, hours, directions, reviews and events for every store."],
  [PartyPopper, "Grand-opening & event campaigns", "We plan launch and community events and post them on your store page."],
  [Megaphone, "Digital advertising assistance", "Help with local digital ads to bring nearby customers in."],
  [TrendingUp, "Customer acquisition resources", "Promotions, signage and tools that turn traffic into activations."],
];

const DEALERS_FIRST = [
  [Plug, "Seamless POS integration", "Your point-of-sale connects with our systems, so sales and activations flow through without double entry."],
  [PackageCheck, "Automated inventory tracking", "Stock updates as you sell, so you always know what's on the shelf and what to reorder."],
  [Coins, "Daily sales & long-term residuals", "Earn on every day's sales and build residual income that keeps growing month after month."],
  [LifeBuoy, "Support at your fingertips", "Find answers anytime in the dealer portal, or talk with our industry veterans when you need a hand."],
  [Clock, "Round-the-clock activation help", "Activation issue at 9 AM or 9 PM? Get immediate troubleshooting, any time of day."],
];

const H2 = ({ eyebrow, title, sub }) => (
  <div className="mb-8 max-w-3xl">
    <p className="label-style text-teal mb-2">{eyebrow}</p>
    <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-2">{title}</h2>
    {sub && <p className="text-slate-600 font-body">{sub}</p>}
  </div>
);

const Cards = ({ items, cols = "lg:grid-cols-4" }) => (
  <div className={`grid sm:grid-cols-2 ${cols} gap-4`}>
    {items.map(([Icon, t, d, to]) => {
      const body = (<><Icon className="w-7 h-7 text-teal mb-3" /><h3 className="font-heading font-semibold text-navy mb-1">{t}</h3><p className="text-sm text-slate-600 font-body">{d}</p></>);
      return to ? <Link key={t} to={to} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-teal transition-colors">{body}</Link>
        : <div key={t} className="rounded-2xl border border-slate-200 bg-white p-5">{body}</div>;
    })}
  </div>
);

export const ScaleAndSpeed = () => {
  const n = useStoreCount();
  return (
    <section className="py-16 bg-slate-50" data-testid="dealer-scale-speed">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <H2 eyebrow="Every state. Every city." title="We can help you open anywhere in the U.S." sub="Whether you're opening your first Total Wireless store or your tenth, we have a path that fits." />
        <Cards items={OPTIONS} />
        <div className="grid sm:grid-cols-3 gap-4 mt-6" data-testid="dealer-track-record">
          {[[Rocket, `${n}+ stores launched`, "A proven store-launch track record — and more on the way."],
            [Timer, "30–35 day typical setup", "Our step-by-step launch process gets you open faster."],
            [Map, "Nationwide reach", "Support in every state and every city we serve."]].map(([Icon, t, d]) => (
            <div key={t} className="rounded-2xl bg-navy text-white p-5"><Icon className="w-7 h-7 text-teal mb-2" /><p className="font-heading font-bold text-xl">{t}</p><p className="text-sm text-slate-300 font-body">{d}</p></div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const DealersFirst = () => (
  <section className="py-16 bg-white" data-testid="dealer-first">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <H2 eyebrow="Dealers come first" title="Our dealers are our #1 priority"
        sub="Everything we build is designed to help you grow your business — with strong daily sales today and long-term residual income for years to come." />
      <Cards cols="lg:grid-cols-5" items={DEALERS_FIRST} />
    </div>
  </section>
);

export const SupportAndTech = () => (
  <section className="py-16 bg-slate-50" data-testid="dealer-support-tech">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <H2 eyebrow="Dealer support & technology" title="Real people and real tools behind every store" sub="Most master agents hand you a contract. We give you a team and a dealer portal built for growth." />
      <Cards items={SUPPORT} />
      <div className="mt-6 rounded-2xl border border-teal/30 bg-teal/5 p-6">
        <p className="font-heading font-semibold text-navy mb-4">Your dealer portal includes</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PORTAL.map(([Icon, t]) => <p key={t} className="flex items-center gap-2 text-slate-700 font-body text-sm"><Icon className="w-5 h-5 text-teal shrink-0" />{t}</p>)}
        </div>
      </div>
    </div>
  </section>
);

export const MarketingGrowth = () => (
  <section className="py-16 bg-white" data-testid="dealer-marketing-growth">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <H2 eyebrow="Dealer marketing & customer growth" title="We help customers find your store" sub="Visibility drives activations. We put marketing support behind every store we launch." />
      <Cards items={MARKETING} />
    </div>
  </section>
);

// Full "why Prime Media" block for dealer-facing pages
export const DealerAdvantage = ({ cta = true }) => (
  <div data-testid="dealer-advantage">
    <ScaleAndSpeed />
    <DealersFirst />
    <SupportAndTech />
    <MarketingGrowth />
    {cta && (
      <section className="py-12 bg-navy"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="font-heading font-bold text-xl text-white">Ready to see what's available in your area?</p>
        <BookCallButton />
      </div></section>
    )}
  </div>
);

export const SuccessJourney = () => (
  <section className="py-16 bg-white" data-testid="process-success-journey">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <H2 eyebrow="Experience the success" title="From your first store launch to long-term growth"
        sub="Prime Media Wireless gives you everything you need to make the journey smooth, smart and scalable." />
      <Cards cols="lg:grid-cols-3" items={[
        [Sofa, "Smart store design support", "From furniture to layout, we help you set up an efficient, attractive store that brings in more customers."],
        [Smile, "Stress-free launching process", "With our support team by your side, launching a Total Wireless store is faster, easier and more relaxed than ever."],
        [Handshake, "A reliable partnership", "We build long-term partnerships so you always feel confident and prepared for what's next."],
      ]} />
    </div>
  </section>
);
